import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import {
  Calendar, CalendarDays, CalendarRange, User, UserCheck, Clock,
  Building2, FileText, ChevronRight, ChevronLeft, ChevronDown
} from 'lucide-react';
import { calendarApi, CalendarEvent, CalendarScope } from '../../services/calendarService';
import {
  generateTwoWeeksGrid, groupEventsByDate, getShortHebrewDayName, getEventIcon,
  formatTime, getHebrewMonthName, getHebrewDayName, formatDateParam,
  getEventsForDay, getAssigneeColor, buildAssigneeLegend, isSameDay,
  CalendarCell
} from '../../utils/calendarHelpers';
import { useAuth } from '../../contexts/AuthContext';
import { departmentsApi, DepartmentDto } from '../../services/settingsService';
import { usersApi, UserDto } from '../../services/usersService';
import { toast } from 'react-hot-toast';
import { handleApiError } from '../../utils/errorHandler';

type ViewMode = 'twoWeeks' | 'daily';

const Widget = styled.div`
  background: white;
  border-radius: 16px;
  padding: 28px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);

  @media (max-width: 767px) {
    padding: 16px;
    border-radius: 12px;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 16px;
`;

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const TitleIcon = styled.div`
  display: flex;
  align-items: center;
  color: var(--primary-color);
  filter: saturate(0.7) brightness(0.9);
`;

const Title = styled.h3`
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: var(--text-primary);
`;

const HeaderControls = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

/* Segmented control — reused for view toggle and scope switch */
const Segmented = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 3px;
  background: var(--background-secondary);
  border: 1px solid var(--border-color);
  border-radius: 9px;
`;

const SegButton = styled.button<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 6px 12px;
  border: none;
  border-radius: 6px;
  background: ${props => props.$active ? 'white' : 'transparent'};
  color: ${props => props.$active ? 'var(--primary-color)' : 'var(--text-secondary)'};
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  box-shadow: ${props => props.$active ? '0 1px 2px rgba(0, 0, 0, 0.10)' : 'none'};
  transition: all 0.15s ease;

  &:hover {
    color: var(--primary-color);
  }
`;

const NavControls = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const NavButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 1px solid var(--border-color);
  border-radius: 8px;
  background: white;
  color: var(--text-secondary);
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: var(--primary-color);
    color: var(--primary-color);
    background: #f8fafc;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const DateRangeLabel = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: var(--text-primary);
  min-width: 120px;
  text-align: center;
`;

const TodayButton = styled.button`
  padding: 6px 12px;
  border: 1px solid var(--primary-color);
  border-radius: 6px;
  background: transparent;
  color: var(--primary-color);
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: var(--primary-color);
    color: white;
  }
`;

/* Scope / filter toolbar */
const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  margin-bottom: 14px;
`;

const FilterGroup = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

const FilterLabel = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: var(--text-secondary);
`;

const FilterSelect = styled.select`
  height: 34px;
  padding: 0 10px;
  border: 1px solid var(--border-color);
  border-radius: 8px;
  background: white;
  color: var(--text-primary);
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  min-width: 150px;
  transition: all 0.15s ease;

  &:hover {
    border-color: var(--primary-color);
  }

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px var(--primary-light);
  }
`;

/* Colour-by-assignee legend */
const Legend = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 14px;
  padding: 10px 12px;
  margin-bottom: 14px;
  background: var(--background-secondary);
  border: 1px solid var(--border-color);
  border-radius: 10px;
`;

const LegendItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 500;
  color: var(--text-primary);
`;

const LegendSwatch = styled.span`
  width: 12px;
  height: 12px;
  border-radius: 3px;
  flex-shrink: 0;
`;

const LoadingBox = styled.div`
  text-align: center;
  padding: 48px;
  color: var(--text-secondary);
`;

// עוטף את שתי השבועות: במסכים צרים הרשת אינה נדחסת אלא נגללת לרוחב,
// כך שכותרות הימים ותאי הימים נשארים מיושרים זה מול זה.
const GridScroller = styled.div`
  @media (max-width: 767px) {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    margin: 0 -4px;
    padding: 0 4px;
  }
`;

const WeekGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 6px;
  margin-bottom: 12px;

  @media (max-width: 767px) {
    /* 7 עמודות ברוחב מינימלי קריא — הגלילה מתבצעת ב-GridScroller */
    grid-template-columns: repeat(7, minmax(92px, 1fr));
    min-width: 660px;
    gap: 5px;
  }
`;

const DayHeaderCell = styled.div`
  text-align: center;
  font-size: 11px;
  font-weight: 600;
  color: var(--text-tertiary);
  padding: 6px 0;
  text-transform: uppercase;
  letter-spacing: 0.3px;
`;

const DayCell = styled.div<{ $isToday: boolean; $hasEvents: boolean }>`
  height: 160px;
  padding: 10px;
  background: ${props => props.$isToday ? '#f8fafc' : 'white'};
  border: 1px solid var(--border-color);
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.2s ease;

  ${props => props.$isToday && `
    border-color: var(--primary-color);
    box-shadow: inset 0 0 0 1px rgba(0, 142, 162, 0.25);
  `}

  &:hover {
    background: ${props => props.$isToday ? '#f0f7ff' : '#f8fafc'};
    border-color: ${props => props.$isToday ? 'var(--primary-color)' : '#c0c6cc'};
  }

  @media (max-width: 767px) {
    height: 132px;
    padding: 8px;
  }
`;

const DayHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const DayNumber = styled.div<{ $isToday: boolean }>`
  font-size: 15px;
  font-weight: ${props => props.$isToday ? '700' : '500'};
  color: ${props => props.$isToday ? 'var(--primary-color)' : 'var(--text-primary)'};
`;

const TodayBadge = styled.span`
  font-size: 9px;
  font-weight: 600;
  color: white;
  background: var(--primary-color);
  padding: 2px 6px;
  border-radius: 4px;
`;

const EventsContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
  overflow-y: auto;
  margin-top: 6px;

  &::-webkit-scrollbar {
    width: 3px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: var(--border-color);
    border-radius: 2px;
  }
`;

const EventPill = styled.div<{ $color: string }>`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 5px 8px;
  background: transparent;
  border-right: 3px solid ${props => props.$color};
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: #f5f7f9;
  }
`;

const EventPillIcon = styled.span<{ $color: string }>`
  display: flex;
  align-items: center;
  color: ${props => props.$color};
  filter: saturate(0.8) brightness(0.95);
`;

const EventPillText = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: var(--text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
`;

const MoreButton = styled.button`
  padding: 3px 8px;
  background: #f5f7f9;
  border: none;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 500;
  color: var(--text-secondary);
  cursor: pointer;
  transition: all 0.2s ease;
  align-self: flex-start;

  &:hover {
    background: var(--primary-color);
    color: white;
  }
`;

/* Daily agenda view */
const AgendaWrap = styled.div``;

const AgendaDayHeader = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  padding-bottom: 10px;
  margin-bottom: 12px;
  border-bottom: 1px solid var(--border-color);
`;

const AgendaDayTitle = styled.h4`
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: var(--text-primary);
`;

const AgendaCount = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: var(--text-secondary);
  white-space: nowrap;
`;

const AgendaEmpty = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 48px 20px;
  color: var(--text-tertiary);
  font-size: 14px;
`;

const AgendaList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 620px;
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: var(--border-color);
    border-radius: 3px;
  }
`;

const AgendaItem = styled.div<{ $color: string }>`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 12px 14px;
  background: white;
  border: 1px solid var(--border-color);
  border-right: 4px solid ${props => props.$color};
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: #f8fafc;
    border-color: #c0c6cc;
    border-right-color: ${props => props.$color};
    transform: translateX(-2px);
  }
`;

const AgendaTime = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: var(--text-primary);
  min-width: 48px;
  text-align: center;
  font-variant-numeric: tabular-nums;
`;

const AgendaIcon = styled.div<{ $color: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 8px;
  color: ${props => props.$color};
  background: ${props => props.$color}14;
  flex-shrink: 0;
`;

const AgendaBody = styled.div`
  flex: 1;
  min-width: 0;
`;

const AgendaTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 3px;
`;

const AgendaTitle = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const AgendaType = styled.span`
  font-size: 10px;
  font-weight: 600;
  color: var(--text-secondary);
  background: var(--background-secondary);
  padding: 2px 7px;
  border-radius: 10px;
  white-space: nowrap;
  flex-shrink: 0;
`;

const AgendaMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px 14px;
  color: var(--text-secondary);
`;

const AgendaMetaItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
`;

const InfoRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  color: var(--text-secondary);
`;

const InfoIcon = styled.span`
  display: inline-flex;
  align-items: center;
  color: var(--text-secondary);
`;

const ModalOverlay = styled.div<{ $isOpen: boolean }>`
  display: ${props => props.$isOpen ? 'flex' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1000;
  align-items: center;
  justify-content: center;
  padding: 20px;
`;

const ModalContent = styled.div`
  background: var(--surface-color);
  border-radius: var(--border-radius-lg);
  padding: 24px;
  max-width: 600px;
  width: 100%;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 1px solid var(--border-color);
`;

const ModalTitle = styled.h3`
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  color: var(--text-primary);
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  color: var(--text-secondary);
  cursor: pointer;
  padding: 4px 8px;
  line-height: 1;
  transition: color 0.2s ease;

  &:hover {
    color: var(--text-primary);
  }
`;

const ModalEventList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ModalEventItem = styled.div<{ color: string }>`
  padding: 12px;
  border-right: 4px solid ${props => props.color};
  background: transparent;
  border-radius: var(--border-radius-md);
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #f5f7f9;
    transform: translateX(-2px);
  }
`;

const ModalEventHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
`;

const ModalEventTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
  display: flex;
  align-items: center;
  gap: 6px;
`;

const ModalEventInfo = styled.div`
  font-size: 14px;
  color: var(--text-secondary);
  line-height: 1.6;
`;

const CalendarWidget: React.FC = () => {
  const navigate = useNavigate();
  const { isManager, departmentIds } = useAuth();

  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [loading, setLoading] = useState(true);

  // View + navigation
  const [viewMode, setViewMode] = useState<ViewMode>('twoWeeks');
  const [weekOffset, setWeekOffset] = useState(0);
  const [selectedDay, setSelectedDay] = useState<Date>(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  });

  // Scope + filters (default scope is always "Mine")
  const [scope, setScope] = useState<CalendarScope>('Mine');
  const [selectedDepartmentId, setSelectedDepartmentId] = useState<number | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [departments, setDepartments] = useState<DepartmentDto[]>([]);
  const [staff, setStaff] = useState<UserDto[]>([]);

  // Day-events modal (two-week view "+X more" / pill click)
  const [modalOpen, setModalOpen] = useState(false);
  const [modalEvents, setModalEvents] = useState<CalendarEvent[]>([]);
  const [modalDate, setModalDate] = useState<Date | null>(null);

  const latestReq = useRef(0);

  // Departments limited to the user's own when Employee; all when Manager
  const availableDepartments = useMemo(() => {
    const active = departments.filter(d => d.isActive);
    if (isManager) return active;
    const mine = new Set(departmentIds);
    return active.filter(d => mine.has(d.id));
  }, [departments, isManager, departmentIds]);

  const canUseDepartment = availableDepartments.length > 0;

  // Staff options — narrowed to the selected department when scope=Department
  const staffOptions = useMemo(() => {
    if (scope === 'Department' && selectedDepartmentId) {
      return staff.filter(u => u.departmentIds?.includes(selectedDepartmentId));
    }
    return staff;
  }, [staff, scope, selectedDepartmentId]);

  const legend = useMemo(
    () => (scope === 'Mine' ? [] : buildAssigneeLegend(events)),
    [scope, events]
  );

  // "Mine" keeps colour-by-type (server colour); broad views colour by assignee
  const colorForEvent = useCallback(
    (event: CalendarEvent): string =>
      scope === 'Mine' ? event.color : getAssigneeColor(event.assignedUserId),
    [scope]
  );

  // Load departments + staff once (non-blocking — the calendar still works without them)
  useEffect(() => {
    let active = true;
    departmentsApi.getAll()
      .then(data => { if (active) setDepartments(data); })
      .catch(() => setDepartments([]));
    usersApi.getStaff()
      .then(data => { if (active) setStaff(data); })
      .catch(() => setStaff([]));
    return () => { active = false; };
  }, []);

  // When switching to Department scope, make sure a department is selected
  useEffect(() => {
    if (scope === 'Department' && selectedDepartmentId == null && availableDepartments.length > 0) {
      setSelectedDepartmentId(availableDepartments[0].id);
    }
  }, [scope, selectedDepartmentId, availableDepartments]);

  const loadEvents = useCallback(async () => {
    const reqId = ++latestReq.current;
    try {
      setLoading(true);

      let start: Date;
      let end: Date;
      if (viewMode === 'daily') {
        start = new Date(selectedDay);
        start.setHours(0, 0, 0, 0);
        end = new Date(start);
      } else {
        const today = new Date();
        start = new Date(today);
        start.setDate(today.getDate() - today.getDay() + (weekOffset * 7));
        start.setHours(0, 0, 0, 0);
        end = new Date(start);
        end.setDate(start.getDate() + 13);
      }

      const data = await calendarApi.getCalendarEvents(
        formatDateParam(start),
        formatDateParam(end),
        {
          scope,
          departmentIds: scope === 'Department' && selectedDepartmentId ? [selectedDepartmentId] : undefined,
          userId: scope !== 'Mine' && selectedUserId ? selectedUserId : undefined
        }
      );

      if (reqId === latestReq.current) setEvents(data);
    } catch (error) {
      if (reqId === latestReq.current) {
        toast.error(handleApiError(error, 'שגיאה בטעינת אירועי היומן'), { id: 'calendar-load' });
      }
    } finally {
      if (reqId === latestReq.current) setLoading(false);
    }
  }, [viewMode, selectedDay, weekOffset, scope, selectedDepartmentId, selectedUserId]);

  // Re-fetch whenever scope / department / employee / week offset / view / day changes
  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  const selectScope = (next: CalendarScope) => {
    setSelectedUserId(null);
    if (next === 'Department') {
      setSelectedDepartmentId(prev => prev ?? availableDepartments[0]?.id ?? null);
    }
    setScope(next);
  };

  const onDepartmentChange = (id: number) => {
    setSelectedUserId(null);
    setSelectedDepartmentId(id);
  };

  const getDateRangeLabel = (): string => {
    const today = new Date();
    const sunday = new Date(today);
    sunday.setDate(today.getDate() - today.getDay() + (weekOffset * 7));

    const endDate = new Date(sunday);
    endDate.setDate(sunday.getDate() + 13);

    const startMonth = getHebrewMonthName(sunday.getMonth());
    const endMonth = getHebrewMonthName(endDate.getMonth());

    if (sunday.getMonth() === endDate.getMonth()) {
      return `${sunday.getDate()}-${endDate.getDate()} ${startMonth}`;
    }
    return `${sunday.getDate()} ${startMonth} - ${endDate.getDate()} ${endMonth}`;
  };

  const getDayLabelShort = (): string =>
    `${selectedDay.getDate()} ${getHebrewMonthName(selectedDay.getMonth())}`;

  const handlePrev = () => setWeekOffset(prev => prev - 2);
  const handleNext = () => setWeekOffset(prev => prev + 2);

  const shiftDay = (delta: number) =>
    setSelectedDay(prev => {
      const next = new Date(prev);
      next.setDate(prev.getDate() + delta);
      next.setHours(0, 0, 0, 0);
      return next;
    });

  const handleTodayDay = () => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    setSelectedDay(d);
  };

  const handleToday = () => {
    if (viewMode === 'twoWeeks') setWeekOffset(0);
    else handleTodayDay();
  };

  const openDay = (date: Date) => {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    setSelectedDay(d);
    setViewMode('daily');
  };

  const eventsMap = useMemo(() => groupEventsByDate(events), [events]);
  const cells = useMemo(() => generateTwoWeeksGrid(eventsMap, weekOffset), [eventsMap, weekOffset]);
  const dailyEvents = useMemo(() => getEventsForDay(events, selectedDay), [events, selectedDay]);
  const dayNames = [0, 1, 2, 3, 4, 5, 6].map(getShortHebrewDayName);

  const isTodaySelected = isSameDay(selectedDay, new Date());
  const showTodayButton = viewMode === 'twoWeeks' ? weekOffset !== 0 : !isTodaySelected;

  const handleMoreClick = (dayEvents: CalendarEvent[], date: Date) => {
    setModalEvents(dayEvents);
    setModalDate(date);
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setModalEvents([]);
    setModalDate(null);
  };

  const navigateToEvent = (event: CalendarEvent) => {
    // Lead reminders navigate to the leads list and open the lead's reminders modal
    // (leads have no dedicated management page). The lead id is passed via navigation
    // state to avoid clashing with the leads page URL filter sync.
    if (event.entityType === 1) {
      if (event.entityId) {
        navigate('/leads', { state: { openReminderLeadId: event.entityId } });
      } else {
        navigate('/leads');
      }
    } else if (event.clientId) {
      navigate(`/clients/${event.clientId}/manage`);
    }
  };

  const handleModalEventClick = (event: CalendarEvent) => {
    handleModalClose();
    navigateToEvent(event);
  };

  const renderDayCell = (cell: CalendarCell, key: React.Key) => (
    <DayCell
      key={key}
      $isToday={cell.isToday}
      $hasEvents={cell.events.length > 0}
      onClick={() => openDay(cell.date)}
      title="פתח תצוגה יומית"
    >
      <DayHeader>
        <DayNumber $isToday={cell.isToday}>{cell.date.getDate()}</DayNumber>
        {cell.isToday && <TodayBadge>היום</TodayBadge>}
      </DayHeader>

      <EventsContainer>
        {cell.displayedEvents.map(event => {
          const color = colorForEvent(event);
          return (
            <EventPill
              key={event.id}
              $color={color}
              onClick={(e) => { e.stopPropagation(); handleMoreClick(cell.events, cell.date); }}
            >
              <EventPillIcon $color={color}>
                {getEventIcon(event, 13)}
              </EventPillIcon>
              <EventPillText>
                {event.clientName || event.title}
              </EventPillText>
            </EventPill>
          );
        })}

        {cell.hasMore && (
          <MoreButton onClick={(e) => { e.stopPropagation(); handleMoreClick(cell.events, cell.date); }}>
            +{cell.events.length - cell.displayedEvents.length} נוספים
          </MoreButton>
        )}
      </EventsContainer>
    </DayCell>
  );

  const renderAgenda = () => (
    <AgendaWrap>
      <AgendaDayHeader>
        <AgendaDayTitle>
          יום {getHebrewDayName(selectedDay.getDay())}, {selectedDay.getDate()} ב{getHebrewMonthName(selectedDay.getMonth())} {selectedDay.getFullYear()}
        </AgendaDayTitle>
        <AgendaCount>{dailyEvents.length} משימות</AgendaCount>
      </AgendaDayHeader>

      {dailyEvents.length === 0 ? (
        <AgendaEmpty>
          <CalendarDays size={28} />
          <span>אין משימות ליום זה</span>
        </AgendaEmpty>
      ) : (
        <AgendaList>
          {dailyEvents.map(event => {
            const color = colorForEvent(event);
            return (
              <AgendaItem key={event.id} $color={color} onClick={() => navigateToEvent(event)}>
                <AgendaTime>{formatTime(event.startDateTime)}</AgendaTime>
                <AgendaIcon $color={color}>{getEventIcon(event, 18)}</AgendaIcon>
                <AgendaBody>
                  <AgendaTitleRow>
                    <AgendaTitle>{event.title}</AgendaTitle>
                    {event.eventTypeName && <AgendaType>{event.eventTypeName}</AgendaType>}
                  </AgendaTitleRow>
                  <AgendaMeta>
                    {event.clientName && (
                      <AgendaMetaItem><User size={13} />{event.clientName}</AgendaMetaItem>
                    )}
                    {event.assignedUserName && (
                      <AgendaMetaItem><UserCheck size={13} />{event.assignedUserName}</AgendaMetaItem>
                    )}
                    {event.doctorName && (
                      <AgendaMetaItem><Building2 size={13} />{event.doctorName}</AgendaMetaItem>
                    )}
                  </AgendaMeta>
                </AgendaBody>
              </AgendaItem>
            );
          })}
        </AgendaList>
      )}
    </AgendaWrap>
  );

  return (
    <Widget>
      <Header>
        <TitleContainer>
          <TitleIcon>
            <Calendar size={20} />
          </TitleIcon>
          <Title>לוח שנה</Title>
        </TitleContainer>

        <HeaderControls>
          <Segmented>
            <SegButton $active={viewMode === 'twoWeeks'} onClick={() => setViewMode('twoWeeks')}>
              <CalendarRange size={14} /> שבועיים
            </SegButton>
            <SegButton $active={viewMode === 'daily'} onClick={() => setViewMode('daily')}>
              <CalendarDays size={14} /> יומי
            </SegButton>
          </Segmented>

          {viewMode === 'twoWeeks' ? (
            <NavControls>
              <NavButton onClick={handlePrev} title="שבועיים קודמים">
                <ChevronRight size={16} />
              </NavButton>
              <DateRangeLabel>{getDateRangeLabel()}</DateRangeLabel>
              <NavButton onClick={handleNext} title="שבועיים הבאים">
                <ChevronLeft size={16} />
              </NavButton>
            </NavControls>
          ) : (
            <NavControls>
              <NavButton onClick={() => shiftDay(-1)} title="יום קודם">
                <ChevronRight size={16} />
              </NavButton>
              <DateRangeLabel>{getDayLabelShort()}</DateRangeLabel>
              <NavButton onClick={() => shiftDay(1)} title="יום הבא">
                <ChevronLeft size={16} />
              </NavButton>
            </NavControls>
          )}

          {showTodayButton && (
            <TodayButton onClick={handleToday}>היום</TodayButton>
          )}
        </HeaderControls>
      </Header>

      {/* Scope switch + per-employee filter */}
      <Toolbar>
        <Segmented>
          <SegButton $active={scope === 'Mine'} onClick={() => selectScope('Mine')}>
            שלי
          </SegButton>
          {canUseDepartment && (
            <SegButton $active={scope === 'Department'} onClick={() => selectScope('Department')}>
              מחלקה <ChevronDown size={13} />
            </SegButton>
          )}
          {isManager && (
            <SegButton $active={scope === 'All'} onClick={() => selectScope('All')}>
              הכל
            </SegButton>
          )}
        </Segmented>

        {scope === 'Department' && (
          <FilterGroup>
            <FilterLabel>מחלקה</FilterLabel>
            <FilterSelect
              value={selectedDepartmentId ?? ''}
              onChange={(e) => onDepartmentChange(Number(e.target.value))}
            >
              {availableDepartments.map(d => (
                <option key={d.id} value={d.id}>{d.name}</option>
              ))}
            </FilterSelect>
          </FilterGroup>
        )}

        {scope !== 'Mine' && (
          <FilterGroup>
            <FilterLabel>עובד</FilterLabel>
            <FilterSelect
              value={selectedUserId ?? ''}
              onChange={(e) => setSelectedUserId(e.target.value ? Number(e.target.value) : null)}
            >
              <option value="">כל העובדים</option>
              {staffOptions.map(u => (
                <option key={u.id} value={u.id}>{u.fullName}</option>
              ))}
            </FilterSelect>
          </FilterGroup>
        )}
      </Toolbar>

      {/* Colour-by-assignee legend (Department / All views) */}
      {scope !== 'Mine' && legend.length > 0 && (
        <Legend>
          {legend.map(item => (
            <LegendItem key={item.userId}>
              <LegendSwatch style={{ background: item.color }} />
              {item.name}
            </LegendItem>
          ))}
        </Legend>
      )}

      {loading ? (
        <LoadingBox>טוען...</LoadingBox>
      ) : viewMode === 'twoWeeks' ? (
        <GridScroller>
          {/* Week 1 */}
          <WeekGrid>
            {dayNames.map((day, index) => (
              <DayHeaderCell key={index}>{day}</DayHeaderCell>
            ))}
          </WeekGrid>
          <WeekGrid>
            {cells.slice(0, 7).map((cell, index) => renderDayCell(cell, index))}
          </WeekGrid>

          {/* Week 2 */}
          <WeekGrid style={{ marginTop: '12px' }}>
            {dayNames.map((day, index) => (
              <DayHeaderCell key={`week2-${index}`}>{day}</DayHeaderCell>
            ))}
          </WeekGrid>
          <WeekGrid>
            {cells.slice(7, 14).map((cell, index) => renderDayCell(cell, `w2-${index}`))}
          </WeekGrid>
        </GridScroller>
      ) : (
        renderAgenda()
      )}

      {/* Modal for showing all events on a day */}
      <ModalOverlay $isOpen={modalOpen} onClick={handleModalClose}>
        <ModalContent onClick={(e) => e.stopPropagation()}>
          <ModalHeader>
            <ModalTitle>
              {modalDate && `כל האירועים ליום ${modalDate.getDate()}/${modalDate.getMonth() + 1}`}
            </ModalTitle>
            <CloseButton onClick={handleModalClose}>×</CloseButton>
          </ModalHeader>

          <ModalEventList>
            {modalEvents.map(event => (
              <ModalEventItem
                key={event.id}
                color={colorForEvent(event)}
                onClick={() => handleModalEventClick(event)}
              >
                <ModalEventHeader>
                  <ModalEventTitle>
                    <span>{getEventIcon(event)}</span>
                    <span>{event.title}</span>
                  </ModalEventTitle>
                </ModalEventHeader>
                <ModalEventInfo>
                  {event.clientName && (
                    <InfoRow>
                      <InfoIcon><User size={14} /></InfoIcon>
                      {event.clientName}
                    </InfoRow>
                  )}
                  <InfoRow>
                    <InfoIcon><Clock size={14} /></InfoIcon>
                    {formatTime(event.startDateTime)}
                  </InfoRow>
                  {event.assignedUserName && (
                    <InfoRow>
                      <InfoIcon><UserCheck size={14} /></InfoIcon>
                      {event.assignedUserName}
                    </InfoRow>
                  )}
                  {event.doctorName && (
                    <InfoRow>
                      <InfoIcon><Building2 size={14} /></InfoIcon>
                      {event.doctorName}
                    </InfoRow>
                  )}
                  {event.description && (
                    <InfoRow>
                      <InfoIcon><FileText size={14} /></InfoIcon>
                      {event.description}
                    </InfoRow>
                  )}
                </ModalEventInfo>
              </ModalEventItem>
            ))}
          </ModalEventList>
        </ModalContent>
      </ModalOverlay>
    </Widget>
  );
};

export default CalendarWidget;
